import { useEffect, useMemo, useRef, useState } from 'react';
import { AutoCompleteComponent } from '@syncfusion/ej2-react-dropdowns';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import useUtils from 'services/utils/useUtils';

interface FormLocalidadesIncendioProps {
    provinciaCodigoName: string;
    provinciaDescripcionName: string;
    municipioCodigoName: string;
    municipioDescripcionName: string;
    localidadCodigoName: string;
    localidadDescripcionName: string;
    required?: boolean | string;
    disabled?: boolean;
    xl?: number;
    md?: number;
    col?: number;
    title?: string;
}

interface LocalidadIncendioRow {
    Id: string;
    CodigoProvincia: string;
    ProvinciaDescripcion: string;
    CodigoMunicipio: string;
    MunicipioDescripcion: string;
    CodigoLocalidad: string;
    LocalidadDescripcion: string;
}

interface SelectorOption {
    Codigo: string;
    Descripcion: string;
    Row: LocalidadIncendioRow;
}

type SyncfusionDataSourceItem = { [key: string]: Object };

const normalizeValue = (value: unknown) => String(value ?? '').trim();

const isDigitsOnly = (value: string) => /^[0-9]+$/.test(value);

const areCodesEquivalent = (left: unknown, right: unknown) => {
    const leftValue = normalizeValue(left);
    const rightValue = normalizeValue(right);

    if (!leftValue || !rightValue) return false;
    if (leftValue === rightValue) return true;

    return isDigitsOnly(leftValue) && isDigitsOnly(rightValue)
        ? Number(leftValue) === Number(rightValue)
        : false;
};

const getValueByKeys = (source: Record<string, unknown>, keys: string[]) => {
    const normalized = Object.entries(source).map(([key, value]) => [
        key.replace(/[_\s]/g, '').toLowerCase(),
        value,
    ] as const);

    for (const key of keys) {
        const target = key.replace(/[_\s]/g, '').toLowerCase();
        const match = normalized.find(([normalizedKey]) => normalizedKey === target);
        if (match) return match[1];
    }

    return undefined;
};

const extractRows = (response: unknown): Record<string, unknown>[] => {
    if (Array.isArray(response)) return response as Record<string, unknown>[];
    if (!response || typeof response !== 'object') return [];

    const source = response as Record<string, unknown>;
    const candidates = [source.items, source.Items, source.result, source.Result, source.data, source.Data];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
    }

    return [];
};

const uniqueOptions = (
    rows: LocalidadIncendioRow[],
    getCode: (row: LocalidadIncendioRow) => string,
    getDescription: (row: LocalidadIncendioRow) => string
) => {
    const seen = new Set<string>();
    return rows
        .reduce<SelectorOption[]>((options, row) => {
            const code = getCode(row);
            const description = getDescription(row);
            const key = `${code}-${description}`;

            if (!code || !description || seen.has(key)) return options;

            seen.add(key);
            options.push({ Codigo: code, Descripcion: description, Row: row });
            return options;
        }, [])
        .sort((left, right) => left.Descripcion.localeCompare(right.Descripcion, 'es'));
};

const FormLocalidadesIncendio: React.FC<FormLocalidadesIncendioProps> = ({
    provinciaCodigoName,
    provinciaDescripcionName,
    municipioCodigoName,
    municipioDescripcionName,
    localidadCodigoName,
    localidadDescripcionName,
    required = false,
    disabled = false,
    xl = 12,
    md = 12,
    col = 12,
    title = 'Localización afectada por el incendio',
}) => {
    const { getLocalidadesFormCATAFMIncendio } = useUtils();
    const {
        register,
        control,
        getValues,
        setValue,
        clearErrors,
        formState: { errors },
    } = useFormContext();

    const [allData, setAllData] = useState<LocalidadIncendioRow[]>([]);
    const [catalogLoaded, setCatalogLoaded] = useState(false);
    const [catalogLoading, setCatalogLoading] = useState(true);

    const provinciaRef = useRef<AutoCompleteComponent | null>(null);
    const municipioRef = useRef<AutoCompleteComponent | null>(null);
    const localidadRef = useRef<AutoCompleteComponent | null>(null);

    const provinciaCodigo = useWatch({ control, name: provinciaCodigoName, defaultValue: getValues(provinciaCodigoName) });
    const provinciaDescripcion = useWatch({ control, name: provinciaDescripcionName, defaultValue: getValues(provinciaDescripcionName) });
    const municipioCodigo = useWatch({ control, name: municipioCodigoName, defaultValue: getValues(municipioCodigoName) });
    const municipioDescripcion = useWatch({ control, name: municipioDescripcionName, defaultValue: getValues(municipioDescripcionName) });
    const localidadCodigo = useWatch({ control, name: localidadCodigoName, defaultValue: getValues(localidadCodigoName) });
    const localidadDescripcion = useWatch({ control, name: localidadDescripcionName, defaultValue: getValues(localidadDescripcionName) });

    const setIfChanged = (fieldName: string, value: string, shouldValidate = false) => {
        if (getValues(fieldName) !== value) {
            setValue(fieldName, value, { shouldValidate, shouldDirty: shouldValidate });
        }
    };

    const clearLocalidad = (shouldValidate = true) => {
        setIfChanged(localidadCodigoName, '', shouldValidate);
        setIfChanged(localidadDescripcionName, '', shouldValidate);
    };

    const clearMunicipio = (shouldValidate = true) => {
        setIfChanged(municipioCodigoName, '', shouldValidate);
        setIfChanged(municipioDescripcionName, '', shouldValidate);
        clearLocalidad(shouldValidate);
    };

    const isValidRow = (row: LocalidadIncendioRow | undefined): row is LocalidadIncendioRow => (
        !!row?.CodigoProvincia &&
        !!row?.ProvinciaDescripcion &&
        !!row?.CodigoMunicipio &&
        !!row?.MunicipioDescripcion &&
        !!row?.CodigoLocalidad &&
        !!row?.LocalidadDescripcion
    );

    const applyRow = (row: LocalidadIncendioRow, shouldValidate = false) => {
        setIfChanged(provinciaCodigoName, row.CodigoProvincia, shouldValidate);
        setIfChanged(provinciaDescripcionName, row.ProvinciaDescripcion, shouldValidate);
        setIfChanged(municipioCodigoName, row.CodigoMunicipio, shouldValidate);
        setIfChanged(municipioDescripcionName, row.MunicipioDescripcion, shouldValidate);
        setIfChanged(localidadCodigoName, row.CodigoLocalidad, shouldValidate);
        setIfChanged(localidadDescripcionName, row.LocalidadDescripcion, shouldValidate);
        clearErrors([
            provinciaDescripcionName,
            municipioDescripcionName,
            localidadDescripcionName,
        ]);
    };

    useEffect(() => {
        register(provinciaCodigoName);
        register(municipioCodigoName);
        register(localidadCodigoName);
    }, [localidadCodigoName, municipioCodigoName, provinciaCodigoName, register]);

    useEffect(() => {
        let isMounted = true;

        const loadLocalidadesIncendio = async () => {
            try {
                const response = await getLocalidadesFormCATAFMIncendio();
                const rows = extractRows(response);
                const mappedRows = rows
                    .map((row) => ({
                        Id: String(getValueByKeys(row, ['Id', 'id']) ?? ''),
                        CodigoProvincia: String(getValueByKeys(row, ['CodigoProvincia', 'codigoProvincia']) ?? '').trim(),
                        ProvinciaDescripcion: String(getValueByKeys(row, ['ProvinciaDescripcion', 'provinciaDescripcion']) ?? '').trim(),
                        CodigoMunicipio: String(getValueByKeys(row, ['CodigoMunicipio', 'codigoMunicipio']) ?? '').trim(),
                        MunicipioDescripcion: String(getValueByKeys(row, ['MunicipioDescripcion', 'municipioDescripcion']) ?? '').trim(),
                        CodigoLocalidad: String(getValueByKeys(row, ['CodigoLocalidad', 'codigoLocalidad']) ?? '').trim(),
                        LocalidadDescripcion: String(getValueByKeys(row, ['LocalidadDescripcion', 'localidadDescripcion']) ?? '').trim(),
                    }))
                    .filter((row) => row.CodigoProvincia && row.ProvinciaDescripcion && row.CodigoMunicipio && row.MunicipioDescripcion && row.CodigoLocalidad && row.LocalidadDescripcion);

                if (isMounted) {
                    setAllData(mappedRows);
                    setCatalogLoaded(true);
                    console.log('FormLocalidadesIncendio', mappedRows);
                }
            } catch (error) {
                if (isMounted) {
                    setAllData([]);
                    setCatalogLoaded(true);
                    console.error('FormLocalidadesIncendio', error);
                }
            } finally {
                if (isMounted) {
                    setCatalogLoading(false);
                }
            }
        };

        loadLocalidadesIncendio();

        return () => {
            isMounted = false;
        };
    }, []);

    const provincias = useMemo(() => uniqueOptions(allData, row => row.CodigoProvincia, row => row.ProvinciaDescripcion), [allData]);

    const municipios = useMemo(() => {
        const selectedProvincia = normalizeValue(provinciaCodigo);
        if (!selectedProvincia) return [];

        return uniqueOptions(
            allData.filter(row => areCodesEquivalent(row.CodigoProvincia, selectedProvincia)),
            row => row.CodigoMunicipio,
            row => row.MunicipioDescripcion
        );
    }, [allData, provinciaCodigo]);

    const localidades = useMemo(() => {
        const selectedMunicipio = normalizeValue(municipioCodigo);
        if (!selectedMunicipio) return [];

        return uniqueOptions(
            allData.filter(row => areCodesEquivalent(row.CodigoMunicipio, selectedMunicipio)),
            row => row.CodigoLocalidad,
            row => row.LocalidadDescripcion
        );
    }, [allData, municipioCodigo]);

    useEffect(() => {
        if (!catalogLoaded || !allData.length) return;

        const selectedLocalidad = allData.find(row => areCodesEquivalent(row.CodigoLocalidad, localidadCodigo))
            || allData.find(row => row.LocalidadDescripcion === normalizeValue(localidadDescripcion));

        if (selectedLocalidad) {
            applyRow(selectedLocalidad);
            return;
        }

        const selectedMunicipio = allData.find(row => areCodesEquivalent(row.CodigoMunicipio, municipioCodigo))
            || allData.find(row => row.MunicipioDescripcion === normalizeValue(municipioDescripcion));

        if (selectedMunicipio) {
            setIfChanged(provinciaCodigoName, selectedMunicipio.CodigoProvincia);
            setIfChanged(provinciaDescripcionName, selectedMunicipio.ProvinciaDescripcion);
            setIfChanged(municipioCodigoName, selectedMunicipio.CodigoMunicipio);
            setIfChanged(municipioDescripcionName, selectedMunicipio.MunicipioDescripcion);
            clearErrors([provinciaDescripcionName, municipioDescripcionName]);
            return;
        }

        const selectedProvincia = allData.find(row => areCodesEquivalent(row.CodigoProvincia, provinciaCodigo))
            || allData.find(row => row.ProvinciaDescripcion === normalizeValue(provinciaDescripcion));

        if (selectedProvincia) {
            setIfChanged(provinciaCodigoName, selectedProvincia.CodigoProvincia);
            setIfChanged(provinciaDescripcionName, selectedProvincia.ProvinciaDescripcion);
            clearErrors(provinciaDescripcionName);
        }
    }, [catalogLoaded, allData, localidadCodigo, localidadDescripcion, municipioCodigo, municipioDescripcion, provinciaCodigo, provinciaDescripcion, clearErrors, localidadDescripcionName, municipioDescripcionName, provinciaDescripcionName]);

    useEffect(() => {
        if (!catalogLoaded || !normalizeValue(municipioCodigo)) return;

        if (localidades.length === 1) {
            const row = localidades[0].Row;
            setIfChanged(localidadCodigoName, row.CodigoLocalidad, true);
            setIfChanged(localidadDescripcionName, row.LocalidadDescripcion, true);
            clearErrors(localidadDescripcionName);
            return;
        }

        if (normalizeValue(localidadCodigo) && !localidades.some(option => areCodesEquivalent(option.Codigo, localidadCodigo))) {
            clearLocalidad(true);
        }
    }, [catalogLoaded, localidades, municipioCodigo, localidadCodigo, clearErrors, localidadDescripcionName]);

    const isRequired = Boolean(required);
    const getRequiredMessage = (fieldLabel: string) => typeof required === 'string' ? required : `Debe seleccionar ${fieldLabel}`;
    const validateProvince = (value: unknown) => (!isRequired || normalizeValue(value) || normalizeValue(provinciaCodigo) ? true : getRequiredMessage('una provincia'));
    const validateMunicipio = (value: unknown) => (!isRequired || !normalizeValue(provinciaCodigo) || normalizeValue(value) || normalizeValue(municipioCodigo) ? true : getRequiredMessage('un municipio'));
    const validateLocalidad = (value: unknown) => (!isRequired || !normalizeValue(municipioCodigo) || normalizeValue(value) || normalizeValue(localidadCodigo) ? true : getRequiredMessage('una localidad'));

    const provinciaError = get(errors, provinciaDescripcionName)?.message as string | undefined;
    const municipioError = get(errors, municipioDescripcionName)?.message as string | undefined;
    const localidadError = get(errors, localidadDescripcionName)?.message as string | undefined;
    const errorMessage = provinciaError || municipioError || localidadError;

    const openPopupOnFocus = (component: AutoCompleteComponent | null, canOpen: boolean) => {
        if (!canOpen || !component) return;
        component.showPopup();
    };

    const localidadDisplayValue = normalizeValue(localidadDescripcion) || (localidades.length === 1 ? localidades[0].Descripcion : '');
    const provinceDisabled = disabled || catalogLoading || !provincias.length;
    const municipioDisabled = disabled || catalogLoading || !normalizeValue(provinciaCodigo) || !municipios.length;
    const localidadDisabled = disabled || catalogLoading || !normalizeValue(municipioCodigo) || !localidades.length;

    return (
        <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
            <fieldset className={`input-block input-block--code ${errorMessage ? 'error' : ''}`} aria-busy={catalogLoading}>
                <div className="container-leyend">
                    <legend className="input-block--code__legend">{title}</legend>
                </div>

                <div className="row">
                    <div className="col-12 col-md-4 delta-autocomplete" data-input-name={provinciaDescripcionName} onMouseDown={() => openPopupOnFocus(provinciaRef.current, !provinceDisabled)}>
                        <Controller
                            name={provinciaDescripcionName}
                            control={control}
                            defaultValue={getValues(provinciaDescripcionName) || ''}
                            rules={{ validate: validateProvince }}
                            render={({ field }) => (
                                <AutoCompleteComponent
                                    ref={(instance) => { provinciaRef.current = instance; }}
                                    id={provinciaDescripcionName}
                                    dataSource={provincias as unknown as SyncfusionDataSourceItem[]}
                                    fields={{ text: 'Descripcion', value: 'Descripcion' }}
                                    placeholder={catalogLoading ? 'Cargando localidades...' : 'Provincia'}
                                    value={String(provinciaDescripcion ?? field.value ?? '')}
                                    change={(event) => {
                                        const selectedDescription = event?.value as string;
                                        const selectedItem = (event?.itemData as SelectorOption | undefined)
                                            || provincias.find(option => option.Descripcion === selectedDescription);

                                        if (selectedItem) {
                                            setIfChanged(provinciaCodigoName, selectedItem.Codigo, true);
                                            field.onChange(selectedItem.Descripcion);
                                            clearMunicipio(true);
                                            return;
                                        }

                                        setIfChanged(provinciaCodigoName, '', true);
                                        field.onChange(selectedDescription || '');
                                        clearMunicipio(true);
                                    }}
                                    onBlur={field.onBlur}
                                    enabled={!provinceDisabled}
                                    ignoreCase={true}
                                    ignoreAccent={true}
                                    filterType="Contains"
                                    minLength={0}
                                    focus={() => openPopupOnFocus(provinciaRef.current, !provinceDisabled)}
                                />
                            )}
                        />
                        {provinciaError && <p className="error m-0">{provinciaError}</p>}
                    </div>

                    <div className="col-12 col-md-4 delta-autocomplete" data-input-name={municipioDescripcionName} onMouseDown={() => openPopupOnFocus(municipioRef.current, !municipioDisabled)}>
                        <Controller
                            name={municipioDescripcionName}
                            control={control}
                            defaultValue={getValues(municipioDescripcionName) || ''}
                            rules={{ validate: validateMunicipio }}
                            render={({ field }) => (
                                <AutoCompleteComponent
                                    ref={(instance) => { municipioRef.current = instance; }}
                                    id={municipioDescripcionName}
                                    dataSource={municipios as unknown as SyncfusionDataSourceItem[]}
                                    fields={{ text: 'Descripcion', value: 'Descripcion' }}
                                    placeholder={!normalizeValue(provinciaCodigo) ? 'Seleccione provincia' : 'Municipio'}
                                    value={String(municipioDescripcion ?? field.value ?? '')}
                                    change={(event) => {
                                        const selectedDescription = event?.value as string;
                                        const selectedItem = (event?.itemData as SelectorOption | undefined)
                                            || municipios.find(option => option.Descripcion === selectedDescription);

                                        if (selectedItem) {
                                            setIfChanged(municipioCodigoName, selectedItem.Codigo, true);
                                            field.onChange(selectedItem.Descripcion);
                                            clearLocalidad(true);
                                            return;
                                        }

                                        setIfChanged(municipioCodigoName, '', true);
                                        field.onChange(selectedDescription || '');
                                        clearLocalidad(true);
                                    }}
                                    onBlur={field.onBlur}
                                    enabled={!municipioDisabled}
                                    ignoreCase={true}
                                    ignoreAccent={true}
                                    filterType="Contains"
                                    minLength={0}
                                    focus={() => openPopupOnFocus(municipioRef.current, !municipioDisabled)}
                                />
                            )}
                        />
                        {municipioError && <p className="error m-0">{municipioError}</p>}
                    </div>

                    <div className="col-12 col-md-4 delta-autocomplete" data-input-name={localidadDescripcionName} onMouseDown={() => openPopupOnFocus(localidadRef.current, !localidadDisabled)}>
                        <Controller
                            name={localidadDescripcionName}
                            control={control}
                            defaultValue={getValues(localidadDescripcionName) || ''}
                            rules={{ validate: validateLocalidad }}
                            render={({ field }) => (
                                <AutoCompleteComponent
                                    ref={(instance) => { localidadRef.current = instance; }}
                                    id={localidadDescripcionName}
                                    dataSource={localidades as unknown as SyncfusionDataSourceItem[]}
                                    fields={{ text: 'Descripcion', value: 'Descripcion' }}
                                    placeholder={!normalizeValue(municipioCodigo) ? 'Seleccione municipio' : 'Localidad'}
                                    value={String(localidadDisplayValue || field.value || '')}
                                    change={(event) => {
                                        const selectedDescription = event?.value as string;
                                        const selectedItem = (event?.itemData as SelectorOption | undefined)
                                            || localidades.find(option => option.Descripcion === selectedDescription);
                                        const selectedRow = isValidRow(selectedItem?.Row)
                                            ? selectedItem.Row
                                            : allData.find(row => row.LocalidadDescripcion === selectedDescription)
                                                || allData.find(row => areCodesEquivalent(row.CodigoLocalidad, selectedDescription));

                                        if (selectedRow) {
                                            applyRow(selectedRow, true);
                                            field.onChange(selectedRow.LocalidadDescripcion);
                                            return;
                                        }

                                        setIfChanged(localidadCodigoName, '', true);
                                        field.onChange(selectedDescription || '');
                                    }}
                                    onBlur={field.onBlur}
                                    enabled={!localidadDisabled}
                                    ignoreCase={true}
                                    ignoreAccent={true}
                                    filterType="Contains"
                                    minLength={0}
                                    focus={() => openPopupOnFocus(localidadRef.current, !localidadDisabled)}
                                />
                            )}
                        />
                        {localidadError && <p className="error m-0">{localidadError}</p>}
                    </div>
                </div>

                <input type="hidden" {...register(provinciaCodigoName)} />
                <input type="hidden" {...register(municipioCodigoName)} />
                <input type="hidden" {...register(localidadCodigoName)} />
            </fieldset>
        </div>
    );
};

export default FormLocalidadesIncendio;